import {
  Lightbulb,
  FileText,
  Layers,
  Play,
  SkipForward,
  SkipBack,
  Mic,
  Check,
  Sparkles,
  Users,
  Pencil,
  MessageCircle,
  Highlighter,
  Home,
  Search,
} from 'lucide-react';

import andy from './../../../assets/images/askAndy.png';

/* ---------- shared bits ---------- */

const ScreenHead = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 border-b border-white/6 px-4 py-3">
    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/15 text-accent">
      <Icon size={14} />
    </span>
    <span className="text-[0.8rem] font-semibold text-white">{label}</span>
    <span className="ml-auto flex gap-1">
      <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
      <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
      <span className="h-1.5 w-1.5 rounded-full bg-accent/70" />
    </span>
  </div>
);

const Bar = ({ w = '100%', c = 'bg-white/10' }) => (
  <span className={`block h-2 rounded-full ${c}`} style={{ width: w }} />
);

/* ---------- Interactive Notes ---------- */

export const NotesMockup = () => (
  <div className="flex h-full w-full flex-col bg-[#080d0b]">
    <ScreenHead icon={FileText} label="Interactive Notes" />
    <div className="flex flex-1 flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-accent/15 px-2 py-1 text-[0.6rem] font-semibold text-accent">
          Biology
        </span>
        <span className="text-[0.7rem] text-white/40">Cell Cycle · Chapter 3</span>
      </div>
      <h4 className="text-sm font-semibold text-white">Phases of Mitosis</h4>
      <div className="flex flex-col gap-2.5">
        <Bar w="92%" />
        <Bar w="80%" />
        <span className="block h-2 w-[70%] rounded-full bg-accent/40" />
        <Bar w="86%" />
        <Bar w="55%" />
      </div>
      <div className="mt-2 flex items-center gap-2 rounded-xl border border-accent/25 bg-accent/8 p-2.5">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/20 text-accent">
          <Highlighter size={13} />
        </span>
        <span className="text-[0.7rem] text-white/70">
          Highlighted <span className="text-accent">3 key terms</span> for review
        </span>
      </div>
    </div>
  </div>
);

/* ---------- Interactive Flashcards ---------- */

export const FlashcardsMockup = () => (
  <div className="flex h-full w-full flex-col bg-[#080d0b]">
    <ScreenHead icon={Layers} label="Interactive Flashcards" />
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-5">
      <div className="relative w-full max-w-[240px]">
        <div className="absolute -right-2.5 -top-2.5 h-full w-full rounded-2xl border border-white/8 bg-white/4" />
        <div className="absolute -right-1.5 -top-1.5 h-full w-full rounded-2xl border border-white/8 bg-white/5" />
        <div className="relative flex h-[150px] flex-col items-center justify-center gap-3 rounded-2xl border border-accent/30 bg-gradient-to-b from-accent/12 to-transparent p-5 text-center">
          <span className="text-[0.6rem] font-semibold uppercase tracking-wide text-accent">
            Tap to flip
          </span>
          <span className="text-base font-semibold text-white">Mitochondria</span>
          <span className="text-[0.72rem] leading-relaxed text-white/55">
            The powerhouse of the cell — generates ATP energy.
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="h-1.5 w-6 rounded-full bg-accent" />
        <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
        <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
        <span className="ml-1 text-[0.65rem] text-white/40">1 / 24</span>
      </div>
    </div>
  </div>
);

/* ---------- Interactive Quizzes (matches the mockup) ---------- */

export const QuizMockup = () => {
  const options = [
    { key: 'A', text: 'The cell grows in size', state: 'wrong' },
    { key: 'B', text: 'DNA replication occurs', state: 'right' },
    { key: 'C', text: 'The nucleus divides', state: 'idle' },
    { key: 'D', text: 'Chromosomes separate', state: 'idle' },
  ];
  return (
    <div className="flex h-full w-full flex-col bg-[#080d0b]">
      <ScreenHead icon={Lightbulb} label="Interactive Quizzes" />
      <div className="flex flex-1 flex-col gap-3 p-4">
        <p className="text-[0.82rem] font-medium leading-snug text-white">
          What happens during the S phase of the cell cycle?
        </p>
        <div className="flex flex-col gap-2">
          {options.map((o) => (
            <div
              key={o.key}
              className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-[0.75rem] text-white/85 quiz-opt ${
                o.state === 'wrong' ? 'quiz-opt--wrong' : ''
              } ${o.state === 'right' ? 'quiz-opt--right' : ''}`}
            >
              <span className="text-white/40">{o.key}.</span>
              <span>{o.text}</span>
              {o.state === 'right' && (
                <Check size={15} className="ml-auto text-accent" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------- Ask Andy (chat) ---------- */

export const ChatMockup = () => (
  <div className="flex h-full w-full flex-col bg-[#080d0b]">
    <ScreenHead icon={MessageCircle} label="Ask Andy" />
    <div className="flex flex-1 flex-col gap-3 p-4">
      <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-accent/15 px-3 py-2 text-[0.72rem] text-white">
        Explain photosynthesis simply
      </div>
      <div className="flex items-end gap-2">
        <img src={andy} alt="Andy" className="h-7 w-7 rounded-full object-cover ring-1 ring-accent/40" />
        <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-white/6 px-3 py-2 text-[0.72rem] leading-relaxed text-white/75">
          Plants turn sunlight, water &amp; CO₂ into glucose and oxygen. Think of
          leaves as tiny solar kitchens. 🌱
        </div>
      </div>
      <div className="mt-auto flex items-center gap-2 rounded-full border border-white/10 bg-white/4 px-3 py-2">
        <span className="text-[0.7rem] text-white/35">Ask anything…</span>
        <span className="ml-auto flex h-6 w-6 items-center justify-center rounded-full bg-accent text-[#04110c]">
          <Sparkles size={12} />
        </span>
      </div>
    </div>
  </div>
);

/* ---------- Podcast player ---------- */

export const PodcastMockup = () => (
  <div className="flex h-full w-full flex-col bg-[#080d0b]">
    <ScreenHead icon={Mic} label="Study Podcast" />
    <div className="flex flex-1 flex-col gap-4 p-4">
      <div className="rounded-2xl border border-white/8 bg-gradient-to-br from-accent/15 to-transparent p-4">
        <span className="text-[0.6rem] font-semibold uppercase tracking-wide text-accent">
          Now Playing
        </span>
        <h4 className="mt-1 text-sm font-semibold text-white">
          Chapter 3 · Newton&apos;s Laws of Motion
        </h4>
        <div className="mt-3 flex items-end gap-[3px]">
          {[10, 18, 26, 16, 30, 22, 34, 20, 28, 14, 24, 32, 18, 26, 12, 22, 30, 16].map(
            (h, i) => (
              <span
                key={i}
                className={`w-[4px] rounded-full ${i < 8 ? 'bg-accent' : 'bg-white/15'}`}
                style={{ height: `${h}px` }}
              />
            ),
          )}
        </div>
        <div className="mt-2 flex justify-between text-[0.6rem] text-white/40">
          <span>4:12</span>
          <span>11:38</span>
        </div>
      </div>
      <div className="flex items-center justify-center gap-6">
        <SkipBack size={16} className="text-white/50" />
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent text-[#04110c]">
          <Play size={18} className="ml-0.5" fill="currentColor" />
        </span>
        <SkipForward size={16} className="text-white/50" />
      </div>
      <div className="mt-1 flex flex-col gap-2">
        <Bar w="90%" c="bg-white/8" />
        <Bar w="78%" c="bg-white/8" />
        <Bar w="84%" c="bg-white/8" />
      </div>
    </div>
  </div>
);

/* ---------- Canvas / Draw ---------- */

export const CanvasMockup = () => (
  <div className="relative flex h-full w-full flex-col bg-[#080d0b]">
    <ScreenHead icon={Pencil} label="Canvas" />
    <div className="relative flex-1 grid-overlay">
      {/* sketched cell diagram */}
      <svg viewBox="0 0 320 200" className="h-full w-full" preserveAspectRatio="xMidYMid meet">
        <ellipse cx="150" cy="100" rx="90" ry="66" fill="none" stroke="#5ED8B2" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="2 0" />
        <circle cx="150" cy="100" r="26" fill="rgba(94,216,178,0.12)" stroke="#5ED8B2" strokeWidth="2" />
        <circle cx="150" cy="100" r="7" fill="#5ED8B2" />
        <path d="M95 70 q-14 -18 -30 -10" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
        <path d="M210 128 q20 14 34 6" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
        <ellipse cx="110" cy="140" rx="14" ry="8" fill="none" stroke="#ffffff" strokeWidth="1.6" opacity="0.4" />
        <text x="40" y="52" fill="#ffffff" opacity="0.6" fontFamily="Caveat, cursive" fontSize="16">
          nucleus
        </text>
        <text x="240" y="150" fill="#5ED8B2" fontFamily="Caveat, cursive" fontSize="16">
          membrane
        </text>
      </svg>
    </div>
    <div className="flex items-center justify-center gap-2 border-t border-white/6 py-2.5">
      {['#5ED8B2', '#ffffff', '#f87171', '#facc15'].map((c) => (
        <span key={c} className="h-4 w-4 rounded-full ring-1 ring-white/20" style={{ background: c }} />
      ))}
      <span className="ml-2 flex h-6 w-6 items-center justify-center rounded-md bg-white/8 text-white/60">
        <Highlighter size={12} />
      </span>
    </div>
  </div>
);

/* ---------- Collaborative Mode ---------- */

export const GroupMockup = () => (
  <div className="flex h-full w-full flex-col bg-[#080d0b]">
    <ScreenHead icon={Users} label="Collaborative Mode" />
    <div className="flex flex-1 flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <div className="flex -space-x-2">
          {['#5ED8B2', '#60a5fa', '#f472b6', '#facc15'].map((c) => (
            <span
              key={c}
              className="flex h-7 w-7 items-center justify-center rounded-full text-[0.6rem] font-bold text-[#04110c] ring-2 ring-[#080d0b]"
              style={{ background: c }}
            >
              {['A', 'M', 'S', 'K'][['#5ED8B2', '#60a5fa', '#f472b6', '#facc15'].indexOf(c)]}
            </span>
          ))}
        </div>
        <span className="text-[0.7rem] text-white/50">4 studying now</span>
        <span className="ml-auto flex items-center gap-1 rounded-full bg-accent/15 px-2 py-1 text-[0.6rem] font-semibold text-accent">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" /> Live
        </span>
      </div>
      <div className="rounded-xl border border-white/8 bg-white/4 p-3">
        <span className="text-[0.7rem] font-medium text-white">Shared Study Set · Genetics</span>
        <div className="mt-2 flex flex-col gap-2">
          <Bar w="88%" />
          <Bar w="72%" />
        </div>
      </div>
      <div className="flex items-start gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#60a5fa] text-[0.55rem] font-bold text-[#04110c]">
          M
        </span>
        <div className="rounded-2xl rounded-tl-sm bg-white/6 px-3 py-2 text-[0.7rem] text-white/75">
          Added 12 new flashcards to the deck 🎉
        </div>
      </div>
    </div>
  </div>
);

/* ---------- Desktop dashboard (laptop screen) ---------- */

export const DashboardMockup = () => (
  <div className="flex h-full w-full bg-[#070f0c] text-white">
    {/* sidebar */}
    <div className="flex w-[24%] flex-col gap-2 border-r border-white/6 bg-[#0a1310] p-2.5">
      <div className="mb-1 flex items-center gap-1.5">
        <span className="h-3.5 w-3.5 rounded bg-accent" />
        <span className="h-1.5 w-9 rounded-full bg-white/40" />
      </div>
      {[Home, FileText, Lightbulb, Layers, Mic].map((Icon, i) => (
        <div
          key={i}
          className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 ${i === 0 ? 'bg-accent/15' : ''}`}
        >
          <Icon size={9} className={i === 0 ? 'text-accent' : 'text-white/40'} />
          <span className={`h-1 flex-1 rounded-full ${i === 0 ? 'bg-accent/50' : 'bg-white/12'}`} />
        </div>
      ))}
      <div className="mt-auto flex items-center gap-1.5">
        <span className="h-4 w-4 rounded-full bg-white/15" />
        <span className="h-1 w-8 rounded-full bg-white/12" />
      </div>
    </div>

    {/* main */}
    <div className="flex flex-1 flex-col gap-2 p-3">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 items-center gap-1.5 rounded-full bg-white/6 px-2 py-1.5">
          <Search size={9} className="text-white/40" />
          <span className="h-1 w-16 rounded-full bg-white/12" />
        </div>
        <span className="h-5 w-5 rounded-full bg-accent/70" />
      </div>

      <div className="flex flex-1 gap-2">
        {/* document */}
        <div className="flex flex-[1.6] flex-col gap-1.5 rounded-lg border border-white/6 bg-white/[0.03] p-2.5">
          <span className="rounded bg-accent/15 px-1.5 py-0.5 text-[0.45rem] font-semibold uppercase text-accent w-fit">
            Biology
          </span>
          <span className="h-2 w-2/3 rounded bg-white/70" />
          <span className="h-1.5 w-full rounded bg-white/12" />
          <span className="h-1.5 w-[94%] rounded bg-white/12" />
          <span className="h-1.5 w-[72%] rounded bg-accent/40" />
          <span className="h-1.5 w-[88%] rounded bg-white/12" />
          <span className="h-1.5 w-[64%] rounded bg-white/12" />
          <span className="h-1.5 w-[80%] rounded bg-white/12" />
        </div>
        {/* Ask Andy panel */}
        <div className="flex flex-1 flex-col gap-1.5 rounded-lg border border-accent/20 bg-accent/[0.06] p-2.5">
          <div className="flex items-center gap-1">
            <img src={andy} alt="Andy" className="h-4 w-4 rounded-full object-cover" />
            <span className="h-1.5 w-8 rounded-full bg-accent/60" />
          </div>
          <span className="h-1.5 w-full rounded bg-white/12" />
          <span className="h-1.5 w-[85%] rounded bg-white/12" />
          <span className="h-1.5 w-[70%] rounded bg-white/12" />
          <div className="mt-auto h-4 rounded-md bg-gradient-to-r from-accent to-accentDark" />
        </div>
      </div>
    </div>
  </div>
);
